import { useEffect, useState } from 'react';
import _ from 'lodash';
import {
    AddCircleOutline as AddCircleOutlineIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Label as LabelIcon,
    Settings as SettingsIcon,
} from '@material-ui/icons';
import { t } from '@lingui/macro';
import { useParams } from 'react-router-dom';

import { useSetLastLocationState } from '../SidebarLocationHistory';
import { useFilterState } from '../../../stores/FilterStateStore';
import { buildUrlParamValue } from '../../../utilities/FilterStateUrlUtilities';
import { useNavigate } from '../../../utilities/PgmUtilities';
import {
    useGroupPages,
    useIsAdminUser,
    useIsSysAdminUser,
    useLandingPage,
    useUser,
} from '../../../stores/SessionStore';
import { useIsIpmDisabled } from '../../../stores/EnvironmentStore';
import { useDashboardContext } from '../../page-framework';
import { useMenu } from '../../../stores/DashboardMenuStore';
import { useDeleteDialog } from '../../form/DeleteDialog';
import { MenuGroup, MenuGroupHistoryItem, MenuGroupItem } from '../menu';
import {
    accessPointFilterId,
    agentFilterId,
    businessServiceFilterId,
    hypervisorFilterId,
    interfaceFilterId,
    networkDeviceFilterId,
    radioFilterId,
    serverFilterId,
    siteFilterId,
    testFilterId,
    timeRangeFilterId,
    virtualMachineFilterId,
    wirelessControllerFilterId,
} from '../../filters';
import {
    accessPointDetailPageId,
    accessPointsPageId,
    agentDetailPageId,
    agentsPageId,
    alertsPageId,
    businessServiceDetailPageId,
    businessServicesOverviewPageId,
    businessServicesPageId,
    hypervisorDetailPageId,
    hypervisorsPageId,
    interfaceDetailPageId,
    interfacesPageId,
    networkDeviceDetailPageId,
    networkDevicesPageId,
    radioDetailPageId,
    radiosPageId,
    serverDetailPageId,
    serversPageId,
    siteDetailPageId,
    sitesOverviewPageId,
    sitesPageId,
    testDetailPageId,
    testsPageId,
    virtualMachineDetailPageId,
    virtualMachinesPageId,
    voipTestNowPageId,
    webTestNowPageId,
    netTestNowPageId,
    wirelessControllerDetailPageId,
    wirelessControllersPageId,
} from '../../pages';
import { usePlmMainMenuPages, useShouldResetMenuHistory } from './PlmMainMenuPages';
import { useDefaultPage } from '../DefaultPage';

const defaultFilters = [timeRangeFilterId];

export default function PlmMainMenu() {
    const { '*': pageId } = useParams();
    const navigate = useNavigate();
    const [deleteDialog, showDeleteDialog] = useDeleteDialog(t`Are you sure you want to delete this dashboard?`);
    const [filterState] = useFilterState();
    const [activeLink, setActiveLink] = useState(undefined);
    const [isIpmDisabled] = useIsIpmDisabled();
    const [isAdminUser] = useIsAdminUser();
    const { dashboardActions } = useDashboardContext();
    const [dashboardMenu, dashboardMenuActions] = useMenu();
    const setLastLocationState = useSetLastLocationState();
    const [isSysAdminUser] = useIsSysAdminUser();
    const plmMainMenuPages = usePlmMainMenuPages();
    const shouldResetMenuHistory = useShouldResetMenuHistory();
    const { defaultPage } = useDefaultPage();
    const [visiblePages] = useGroupPages();
    const [landingPage] = useLandingPage();
    const [currentUser] = useUser();

    const enterpriseMenuGroup = [
        plmMainMenuPages.sitesOverview,
        plmMainMenuPages.sites,
        plmMainMenuPages.businessServicesOverview,
        plmMainMenuPages.businessServices,
        plmMainMenuPages.alerts,
    ];
    const serviceDeliveryGroup = [
        plmMainMenuPages.agents,
        plmMainMenuPages.tests,
        plmMainMenuPages.webTestNow,
        plmMainMenuPages.voipTestNow,
        plmMainMenuPages.netTestNow,
    ];
    const infrastructureMenuGroup = [
        plmMainMenuPages.servers,
        plmMainMenuPages.networkDevices,
        plmMainMenuPages.interfaces,
    ];
    const wirelessInfrastructureMenuGroup = [
        plmMainMenuPages.wirelessControllers,
        plmMainMenuPages.accessPoints,
        plmMainMenuPages.radios,
    ];
    const vmwareInfrastructureMenuGroup = [plmMainMenuPages.hypervisors, plmMainMenuPages.virtualMachines];

    function getDashboardActions(dashboard) {
        const ownedByCurrentUser = dashboard?.userId === currentUser._id;
        const canDelete = ownedByCurrentUser || isSysAdminUser;

        return [
            ...(ownedByCurrentUser
                ? [
                      {
                          id: 'edit-dashboard',
                          icon: <EditIcon />,
                          tooltip: t`Edit this dashboard`,
                          onClick: () => {
                              if (pageId !== dashboard.id) {
                                  navigate(`/${dashboard.id}`, {
                                      state: {
                                          editing: true,
                                      },
                                  });
                              } else {
                                  dashboardActions.setEditing(true);
                              }
                          },
                      },
                  ]
                : []),
            ...(canDelete
                ? [
                      {
                          id: 'delete-dashboard',
                          icon: <DeleteIcon />,
                          tooltip: t`Delete this dashboard`,
                          onClick: () => {
                              showDeleteDialog(() => {
                                  dashboardMenuActions.deleteDashboard(dashboard);

                                  // Navigate to the user's default page if the deleted dashboard is the active page.
                                  if (pageId === dashboard.id) {
                                      let postDeleteUrl = defaultPage;
                                      if (defaultPage.slice(1) === dashboard.id) {
                                          postDeleteUrl = landingPage ? `/${landingPage}` : '/sites-overview';
                                          if (dashboardMenu?.userDashboards?.length > 1) {
                                              const nextDashboardId = dashboardMenu.userDashboards
                                                  .filter((userDash) => userDash.id !== dashboard.id)
                                                  .at(0).id;
                                              postDeleteUrl = `/${nextDashboardId}`;
                                          } else {
                                              postDeleteUrl = visiblePages.length ? postDeleteUrl : '/no-access';
                                          }
                                      }

                                      navigate(postDeleteUrl);
                                  }
                              });
                          },
                      },
                  ]
                : []),
        ];
    }

    useEffect(() => {
        dashboardMenuActions.load();
    }, [dashboardMenuActions]);

    useEffect(() => {
        // Save the active link state in session storage.  This is necessary so that the
        // admin menu back button can return to the correct page.
        if (activeLink) {
            const encodedFilterState = {
                ...(filterState?.encoded || {}),
                ...(activeLink?.encodedFilterState || {}),
            };

            // Our sidebar links now start with a leading slash to avoid relative navigation issues.
            // The page name in portal/ui cannot have a leading slash. We need to drop this
            // in the compatibility layer for portal/ui.
            let page = activeLink.target;
            if (page && page.startsWith('/')) {
                page = page.substr(1);
            }

            setLastLocationState({
                name: 'app.auth.page',
                params: {
                    page,
                    filterState: buildUrlParamValue(_.pick(encodedFilterState, activeLink.filters)),
                },
            });
        }
    }, [activeLink, filterState, setLastLocationState]);

    function handleDashboardsReordered(privateDashboards) {
        const newDashboardMenu = {
            ...dashboardMenu,
            privateDashboards,
        };

        dashboardMenuActions.update(newDashboardMenu);
    }

    function handleItemActiveChange(itemLink, active) {
        // Save the active link state in session storage.  This is necessary so that the
        // admin menu back button can return to the correct page.
        if (active) setActiveLink(itemLink);
    }

    function showMenuGroup(menuItems) {
        return _.some(menuItems, 'viewable');
    }

    return (
        <>
            {showMenuGroup(enterpriseMenuGroup) && (
                <MenuGroup id="enterprise" title={t`Enterprise`} expanded>
                    {plmMainMenuPages.sitesOverview.viewable && (
                        <MenuGroupItem
                            id={sitesOverviewPageId}
                            title={plmMainMenuPages.sitesOverview.title}
                            icon={plmMainMenuPages.sitesOverview.icon}
                            link={{
                                target: `/${plmMainMenuPages.sitesOverview.path}`,
                                filters: defaultFilters,
                            }}
                            onActiveChange={handleItemActiveChange}
                        />
                    )}
                    {plmMainMenuPages.sites.viewable && (
                        <MenuGroupHistoryItem
                            id={sitesPageId}
                            title={plmMainMenuPages.sites.title}
                            icon={plmMainMenuPages.sites.icon}
                            link={{
                                target: `/${plmMainMenuPages.sites.path}`,
                                filters: defaultFilters,
                            }}
                            historyConfig={{
                                filterId: siteFilterId,
                                link: {
                                    target: `/${siteDetailPageId}`,
                                    filters: [...defaultFilters, siteFilterId],
                                },
                                onActiveChange: handleItemActiveChange,
                                shouldResetHistory: shouldResetMenuHistory,
                            }}
                            onActiveChange={handleItemActiveChange}
                        />
                    )}
                    {plmMainMenuPages.businessServicesOverview.viewable && (
                        <MenuGroupItem
                            id={businessServicesOverviewPageId}
                            title={plmMainMenuPages.businessServicesOverview.title}
                            icon={plmMainMenuPages.businessServicesOverview.icon}
                            link={{
                                target: `/${plmMainMenuPages.businessServicesOverview.path}`,
                                filters: defaultFilters,
                            }}
                            onActiveChange={handleItemActiveChange}
                        />
                    )}
                    {plmMainMenuPages.businessServices.viewable && (
                        <MenuGroupHistoryItem
                            id={businessServicesPageId}
                            title={plmMainMenuPages.businessServices.title}
                            icon={plmMainMenuPages.businessServices.icon}
                            link={{
                                target: `/${plmMainMenuPages.businessServices.path}`,
                                filters: defaultFilters,
                            }}
                            historyConfig={{
                                filterId: businessServiceFilterId,
                                link: {
                                    target: `/${businessServiceDetailPageId}`,
                                    filters: [...defaultFilters, businessServiceFilterId],
                                },
                                onActiveChange: handleItemActiveChange,
                                shouldResetHistory: shouldResetMenuHistory,
                            }}
                            onActiveChange={handleItemActiveChange}
                        />
                    )}
                    {plmMainMenuPages.alerts.viewable && (
                        <MenuGroupItem
                            id={alertsPageId}
                            title={plmMainMenuPages.alerts.title}
                            icon={plmMainMenuPages.alerts.icon}
                            link={{
                                target: `/${plmMainMenuPages.alerts.path}`,
                                filters: defaultFilters,
                            }}
                            onActiveChange={handleItemActiveChange}
                        />
                    )}
                </MenuGroup>
            )}
            {showMenuGroup(serviceDeliveryGroup) && (
                <MenuGroup id="service-delivery" title={t`Service Delivery`}>
                    {plmMainMenuPages.agents.viewable && (
                        <MenuGroupHistoryItem
                            id={agentsPageId}
                            title={plmMainMenuPages.agents.title}
                            icon={plmMainMenuPages.agents.icon}
                            link={{
                                target: `/${plmMainMenuPages.agents.path}`,
                                filters: defaultFilters,
                            }}
                            historyConfig={{
                                filterId: agentFilterId,
                                link: {
                                    target: `/${agentDetailPageId}`,
                                    filters: [...defaultFilters, agentFilterId],
                                },
                                onActiveChange: handleItemActiveChange,
                                shouldResetHistory: shouldResetMenuHistory,
                            }}
                            onActiveChange={handleItemActiveChange}
                        />
                    )}
                    {plmMainMenuPages.tests.viewable && (
                        <MenuGroupHistoryItem
                            id={testsPageId}
                            title={plmMainMenuPages.tests.title}
                            icon={plmMainMenuPages.tests.icon}
                            link={{
                                target: `/${plmMainMenuPages.tests.path}`,
                                filters: defaultFilters,
                            }}
                            historyConfig={{
                                filterId: testFilterId,
                                link: {
                                    target: `/${testDetailPageId}`,
                                    filters: [...defaultFilters, testFilterId],
                                },
                                onActiveChange: handleItemActiveChange,
                                shouldResetHistory: shouldResetMenuHistory,
                            }}
                            onActiveChange={handleItemActiveChange}
                        />
                    )}
                    {plmMainMenuPages.webTestNow.viewable && (
                        <MenuGroupItem
                            id={webTestNowPageId}
                            title={plmMainMenuPages.webTestNow.title}
                            icon={plmMainMenuPages.webTestNow.icon}
                            link={{
                                target: `/${plmMainMenuPages.webTestNow.path}`,
                            }}
                            onActiveChange={handleItemActiveChange}
                        />
                    )}
                    {plmMainMenuPages.voipTestNow.viewable && (
                        <MenuGroupItem
                            id={voipTestNowPageId}
                            title={plmMainMenuPages.voipTestNow.title}
                            icon={plmMainMenuPages.voipTestNow.icon}
                            link={{
                                target: `/${plmMainMenuPages.voipTestNow.path}`,
                            }}
                            onActiveChange={handleItemActiveChange}
                        />
                    )}
                    {plmMainMenuPages.netTestNow.viewable && (
                        <MenuGroupItem
                            id={netTestNowPageId}
                            title={plmMainMenuPages.netTestNow.title}
                            icon={plmMainMenuPages.netTestNow.icon}
                            link={{
                                target: `/${plmMainMenuPages.netTestNow.path}`,
                            }}
                            onActiveChange={handleItemActiveChange}
                        />
                    )}
                </MenuGroup>
            )}
            {!isIpmDisabled && (
                <>
                    {showMenuGroup(infrastructureMenuGroup) && (
                        <MenuGroup id="infrastructure" title={t`Infrastructure`}>
                            {plmMainMenuPages.servers.viewable && (
                                <MenuGroupHistoryItem
                                    id={serversPageId}
                                    title={plmMainMenuPages.servers.title}
                                    icon={plmMainMenuPages.servers.icon}
                                    link={{
                                        target: `/${plmMainMenuPages.servers.path}`,
                                        filters: defaultFilters,
                                    }}
                                    historyConfig={{
                                        filterId: serverFilterId,
                                        link: {
                                            target: `/${serverDetailPageId}`,
                                            filters: [...defaultFilters, serverFilterId],
                                        },
                                        onActiveChange: handleItemActiveChange,
                                        shouldResetHistory: shouldResetMenuHistory,
                                    }}
                                    onActiveChange={handleItemActiveChange}
                                />
                            )}
                            {plmMainMenuPages.networkDevices.viewable && (
                                <MenuGroupHistoryItem
                                    id={networkDevicesPageId}
                                    title={plmMainMenuPages.networkDevices.title}
                                    icon={plmMainMenuPages.networkDevices.icon}
                                    link={{
                                        target: `/${plmMainMenuPages.networkDevices.path}`,
                                        filters: defaultFilters,
                                    }}
                                    historyConfig={{
                                        filterId: networkDeviceFilterId,
                                        link: {
                                            target: `/${networkDeviceDetailPageId}`,
                                            filters: [...defaultFilters, networkDeviceFilterId],
                                        },
                                        onActiveChange: handleItemActiveChange,
                                        shouldResetHistory: shouldResetMenuHistory,
                                    }}
                                    onActiveChange={handleItemActiveChange}
                                />
                            )}
                            {plmMainMenuPages.interfaces.viewable && (
                                <MenuGroupHistoryItem
                                    id={interfacesPageId}
                                    title={plmMainMenuPages.interfaces.title}
                                    icon={plmMainMenuPages.interfaces.icon}
                                    link={{
                                        target: `/${plmMainMenuPages.interfaces.path}`,
                                        filters: defaultFilters,
                                    }}
                                    historyConfig={{
                                        filterId: interfaceFilterId,
                                        link: {
                                            target: `/${interfaceDetailPageId}`,
                                            filters: [...defaultFilters, interfaceFilterId],
                                        },
                                        onActiveChange: handleItemActiveChange,
                                        shouldResetHistory: shouldResetMenuHistory,
                                    }}
                                    onActiveChange={handleItemActiveChange}
                                />
                            )}
                        </MenuGroup>
                    )}
                    {showMenuGroup(wirelessInfrastructureMenuGroup) && (
                        <MenuGroup id="wireless-infrastructure" title={t`Wireless Infrastructure`}>
                            {plmMainMenuPages.wirelessControllers.viewable && (
                                <MenuGroupHistoryItem
                                    id={wirelessControllersPageId}
                                    title={plmMainMenuPages.wirelessControllers.title}
                                    icon={plmMainMenuPages.wirelessControllers.icon}
                                    link={{
                                        target: `/${plmMainMenuPages.wirelessControllers.path}`,
                                        filters: defaultFilters,
                                    }}
                                    historyConfig={{
                                        filterId: wirelessControllerFilterId,
                                        link: {
                                            target: `/${wirelessControllerDetailPageId}`,
                                            filters: [...defaultFilters, wirelessControllerFilterId],
                                        },
                                        onActiveChange: handleItemActiveChange,
                                        shouldResetHistory: shouldResetMenuHistory,
                                    }}
                                    onActiveChange={handleItemActiveChange}
                                />
                            )}
                            {plmMainMenuPages.accessPoints.viewable && (
                                <MenuGroupHistoryItem
                                    id={accessPointsPageId}
                                    title={plmMainMenuPages.accessPoints.title}
                                    icon={plmMainMenuPages.accessPoints.icon}
                                    link={{
                                        target: `/${plmMainMenuPages.accessPoints.path}`,
                                        filters: defaultFilters,
                                    }}
                                    historyConfig={{
                                        filterId: accessPointFilterId,
                                        link: {
                                            target: `/${accessPointDetailPageId}`,
                                            filters: [...defaultFilters, accessPointFilterId],
                                        },
                                        onActiveChange: handleItemActiveChange,
                                        shouldResetHistory: shouldResetMenuHistory,
                                    }}
                                    onActiveChange={handleItemActiveChange}
                                />
                            )}
                            {plmMainMenuPages.radios.viewable && (
                                <MenuGroupHistoryItem
                                    id={radiosPageId}
                                    title={plmMainMenuPages.radios.title}
                                    icon={plmMainMenuPages.radios.icon}
                                    link={{
                                        target: `/${plmMainMenuPages.radios.path}`,
                                        filters: defaultFilters,
                                    }}
                                    historyConfig={{
                                        filterId: radioFilterId,
                                        link: {
                                            target: `/${radioDetailPageId}`,
                                            filters: [...defaultFilters, radioFilterId],
                                        },
                                        onActiveChange: handleItemActiveChange,
                                        shouldResetHistory: shouldResetMenuHistory,
                                    }}
                                    onActiveChange={handleItemActiveChange}
                                />
                            )}
                        </MenuGroup>
                    )}
                    {showMenuGroup(vmwareInfrastructureMenuGroup) && (
                        <MenuGroup id="vmware-infrastructure" title={t`VMware® Infrastructure`}>
                            {plmMainMenuPages.hypervisors.viewable && (
                                <MenuGroupHistoryItem
                                    id={hypervisorsPageId}
                                    title={plmMainMenuPages.hypervisors.title}
                                    icon={plmMainMenuPages.hypervisors.icon}
                                    link={{
                                        target: `/${plmMainMenuPages.hypervisors.path}`,
                                        filters: defaultFilters,
                                    }}
                                    historyConfig={{
                                        filterId: hypervisorFilterId,
                                        link: {
                                            target: `/${hypervisorDetailPageId}`,
                                            filters: [...defaultFilters, hypervisorFilterId],
                                        },
                                        onActiveChange: handleItemActiveChange,
                                        shouldResetHistory: shouldResetMenuHistory,
                                    }}
                                    onActiveChange={handleItemActiveChange}
                                />
                            )}
                            {plmMainMenuPages.virtualMachines.viewable && (
                                <MenuGroupHistoryItem
                                    id={virtualMachinesPageId}
                                    title={plmMainMenuPages.virtualMachines.title}
                                    icon={plmMainMenuPages.virtualMachines.icon}
                                    link={{
                                        target: `/${plmMainMenuPages.virtualMachines.path}`,
                                        filters: defaultFilters,
                                    }}
                                    historyConfig={{
                                        filterId: virtualMachineFilterId,
                                        link: {
                                            target: `/${virtualMachineDetailPageId}`,
                                            filters: [...defaultFilters, virtualMachineFilterId],
                                        },
                                        onActiveChange: handleItemActiveChange,
                                        shouldResetHistory: shouldResetMenuHistory,
                                    }}
                                    onActiveChange={handleItemActiveChange}
                                />
                            )}
                        </MenuGroup>
                    )}
                </>
            )}
            <MenuGroup
                id="user-dashboards"
                title={t`Private Dashboards`}
                enableReordering
                onItemsReordered={handleDashboardsReordered}
                actions={[
                    {
                        id: 'create-dashboard',
                        icon: <AddCircleOutlineIcon />,
                        tooltip: t`Create a new dashboard`,
                        onClick: () => {
                            navigate('/new-dashboard', {
                                state: {
                                    editing: true,
                                },
                            });
                        },
                    },
                ]}
            >
                {dashboardMenu?.privateDashboards?.map((dashboard) => {
                    return (
                        <MenuGroupItem
                            key={dashboard.id}
                            id={dashboard.id}
                            title={dashboard.title}
                            hoverText={dashboard.title}
                            icon={<LabelIcon />}
                            actions={getDashboardActions(dashboard)}
                            link={{
                                target: `/${dashboard.id}`,
                                filters: defaultFilters,
                            }}
                            dragItem={dashboard}
                            onActiveChange={handleItemActiveChange}
                        />
                    );
                }) || null}
            </MenuGroup>
            <MenuGroup id="shared-dashboards" title={t`Shared Dashboards`}>
                {dashboardMenu?.sharedDashboards
                    ?.sort((a, b) => a.title.localeCompare(b.title))
                    .map((dashboard) => {
                        return (
                            <MenuGroupItem
                                key={dashboard.id}
                                id={dashboard.id}
                                title={dashboard.title}
                                hoverText={dashboard.title}
                                icon={<LabelIcon />}
                                actions={getDashboardActions(dashboard)}
                                link={{
                                    target: `/${dashboard.id}`,
                                    filters: defaultFilters,
                                }}
                                onActiveChange={handleItemActiveChange}
                            />
                        );
                    }) || null}
            </MenuGroup>
            <MenuGroup id="administration-group">
                {plmMainMenuPages.reports.viewable && (
                    <MenuGroupItem
                        id="schedule-reports"
                        title={plmMainMenuPages.reports.title}
                        icon={plmMainMenuPages.reports.icon}
                        link={{
                            target: 'reports',
                        }}
                        onActiveChange={handleItemActiveChange}
                    />
                )}
                {isAdminUser && (
                    <MenuGroupItem
                        id="administration"
                        title={t`Administration`}
                        icon={<SettingsIcon />}
                        link={{
                            target: '/admin/',
                        }}
                    />
                )}
            </MenuGroup>
            {deleteDialog}
        </>
    );
}
